import { AddressApi } from 'services';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { get, filter, first, isNil, map } from 'lodash';
import { toastMessages } from 'config/constants';
import { useCompany } from './CompanyContext';
import toast from 'react-hot-toast';
import useGenericContext from 'hooks/useGenericContext';

const AddressContext = createContext(undefined);
const AddressFunctionsContext = createContext(undefined);

function AddressProvider({ children }) {
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(true);
  const [addressesSaving, setAddressesSaving] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);

  const { currentCompany } = useCompany();

  const loadAddresses = useCallback(async () => {
    if (isNil(currentCompany?.id)) return;

    try {
      const { data } = await AddressApi.listCompanyAddresses(currentCompany.id);

      setAddresses(data);
      if (data.length !== 0) setCurrentAddress(first(data));
    } catch (e) {
      if (get(e, 'response.status') !== 401) toast.error(toastMessages.error.FETCH_ADDRESSES);
    }

    setAddressesLoading(false);
  }, [currentCompany?.id]);

  const createAddress = useCallback(
    async (address) => {
      setAddressesSaving(true);

      try {
        const { data } = await AddressApi.createCompanyAddress(currentCompany?.id, address);
        setAddresses((prev) => [...prev, data]);
        setAddressesSaving(false);
        setTimeout(loadAddresses);

        toast.success(toastMessages.success.CREATE_ADDRESS);
        return { created: true, item: data };
      } catch (e) {
        toast.error(toastMessages.error.CREATE_ADDRESS);
        return { created: false };
      }
    },
    [currentCompany?.id, loadAddresses],
  );

  const updateAddress = useCallback(
    async (address) => {
      setAddressesSaving(true);
      try {
        const response = await AddressApi.updateAddress(address.id, address);

        // Manage conflict response
        if (response.status === 'CONFLICT') {
          return { conflict: true, updated: false };
        }

        setAddresses((prev) => {
          return map(prev, (address) => {
            if (address.id === response.data.id) return response.data;
            return address;
          });
        });
        setAddressesSaving(false);
        setTimeout(loadAddresses);

        toast.success(toastMessages.success.UPDATE_ADDRESS);
        return { updated: true, item: response.data };
      } catch (e) {
        toast.error(toastMessages.error.UPDATE_ADDRESS);
        return { updated: false };
      }
    },
    [loadAddresses],
  );

  const removeAddress = useCallback(
    async (addressId, body) => {
      if (currentAddress?.id === addressId) {
        return { removed: false };
      }

      setAddressesSaving(true);
      try {
        const response = await AddressApi.deleteAddress(addressId, body);

        // Manage conflict response
        if (response.status === 'CONFLICT') {
          return { conflict: true, removed: false };
        }

        // Manage NO DELETE response
        if (response.status === 'NO_DELETE') {
          return { removed: false };
        }

        const newAddresses = filter(addresses, (address) => address.id !== addressId);
        if (currentAddress?.id === addressId) setCurrentAddress(first(newAddresses));
        setAddresses(newAddresses);
        setAddressesSaving(false);

        setTimeout(loadAddresses);

        toast.success(toastMessages.success.DELETE_ADDRESS);
        return { removed: true };
      } catch (e) {
        toast.error(toastMessages.error.DELETE_ADDRESS);
        return { removed: false };
      }
    },
    [addresses, currentAddress?.id, loadAddresses],
  );

  useEffect(() => {
    setAddressesLoading(true);
    setTimeout(loadAddresses);
  }, [loadAddresses]);

  const value = useMemo(
    () => ({
      addresses,
      addressesLoading,
      addressesSaving,
      currentAddress,
    }),
    [addresses, addressesLoading, addressesSaving, currentAddress],
  );

  const functions = useMemo(
    () => ({
      createAddress,
      loadAddresses,
      removeAddress,
      updateAddress,
    }),
    [createAddress, loadAddresses, removeAddress, updateAddress],
  );

  return (
    <AddressContext.Provider value={value}>
      <AddressFunctionsContext.Provider value={functions}>{children}</AddressFunctionsContext.Provider>
    </AddressContext.Provider>
  );
}

/**
 * Return the items values.
 *
 * @return {{
 *   addresses: Array,
 *   addressesLoading: boolean,
 *   addressesSaving: boolean,
 *   currentAddress: Object
 *  }}
 */
function useAddresses() {
  return useGenericContext(AddressContext, 'useAddresses cannot be used outside of a AddressProvider component.');
}

/**
 * Return the functions manipulating items.
 */
function useAddressesFunctions() {
  return useGenericContext(
    AddressFunctionsContext,
    'useAddressesFunctions cannot be used outside of a AddressProvider component',
  );
}

export { AddressProvider, useAddresses, useAddressesFunctions };
